<template>
  <div>
    <v-menu lazy :allow-overflow="true" :close-on-content-click="false" v-model="openCalendar" transition="scale-transition" offset-y full-width :nudge-right="40" max-width="500px" min-width="290px">
      <v-text-field v-date-mask slot="activator" v-model="textValue" :hint="hint" persistent-hint :label="label" prepend-icon="mdi-calendar" :error-messages="errorMessages" :readonly="readonly" :disabled="disabled" :loading="loading" :class="clazz" @blur="textChange()"></v-text-field>
      <v-date-picker :readonly="calReadonly" v-model="calValue" landscape @input="calChange()" :min="calMin" :max="calMax">
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
/**
 * Date picker for Maverick System
 * its a wrapper around vuetify date picker (which is quite troublesome to use as we are using diff date format)
 * Note: Vuetify Calendar format is fixed at yyyy-MM-dd
 */
import toDate from 'date-fns/toDate'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import { displayDateFormat } from '@/utils/date'

export default {
  data() {
    return {
      openCalendar: false,
      textValue: undefined,
      calValue: undefined,
      calFormat: 'yyyy-MM-dd'
    }
  },
  computed: {
    calReadonly() {
      if (this.readonly || this.disabled) {
        return true
      } else {
        return false
      }
    },
    calMin() {
      // Convert min to calendar readable date format
      if (this.min) {
        return format(this.min, this.calFormat)
      } else {
        return undefined
      }
    },
    calMax() {
      if (this.max) {
        return format(this.max, this.calFormat)
      } else {
        return undefined
      }
    }
  },
  watch: {
    'value': function (value) {
      this.doInit(value)
    }
  },
  methods: {
    doInit(dateVal) {
      // Format the value here
      if (dateVal) {
        this.textValue = format(dateVal, displayDateFormat)
        this.calValue = format(dateVal, this.calFormat)
      } else {
        this.textValue = undefined
        this.calValue = undefined
      }
    },
    textChange() {
      if (this.textValue) {
        try {
          let convertDate = parse(this.textValue, displayDateFormat, new Date())
          // If date-fns lib upgrade and error parsing return is not 'Invalid Date', have to change here
          if (!isNaN(convertDate) && this.isValid(convertDate)) {
            // Dont have to convert the calValue here, its taken care of in the watch section
            this.$emit('input', convertDate)
            // To emit blur to trigger vee-validate (seems like the 1st value change does trigger from blur?)
            this.$emit('blur', convertDate)
          } else {
            this.doInit(this.value)
          }
        } catch (err) {
          console.log(err)
          this.doInit(this.value)
        }
      } else {
        this.calValue = undefined
        this.$emit('input', undefined)
        this.$emit('blur', undefined)
      }
    },
    calChange() {
      if (this.calValue) {
        try {
          let convertDate = parse(this.calValue, this.calFormat, new Date())
          if (!isNaN(convertDate)) {
            this.$emit('input', convertDate)
            this.$emit('blur', convertDate)
          } else {
            this.doInit(this.value)
          }
        } catch (err) {
          this.doInit(this.value)
        }
      }
    },
    isValid(dateVal) {
      // Valid ISO date doesnt mean the date calendar is valid (it will throw invalid date if its not supported)
      // No documentation stating the supporting date range
      if (dateVal) {
        const convert = toDate(dateVal)
        if (convert.getFullYear() > 1800) {
          return true
        } else {
          return false
        }
      } else {
        return false
      }
    }
  },
  mounted() {
    this.doInit(this.value)
  },
  props: {
    value: {
      type: [Date, String],
      default: undefined
    },
    label: {
      type: String,
      default: 'Date'
    },
    hint: {
      type: String,
      // To make it look nicer instead of the param (dd/MM/yyyy), we will hardcode to DD/MM/YYYY
      default: 'DD/MM/YYYY'
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default: () => []
    },
    min: {
      type: [Date, String],
      default: undefined
    },
    max: {
      type: [Date, String],
      default: undefined
    },
    clazz: {
      type: String,
      default: undefined
    }
  }
}
</script>

<style>
</style>
